import { z } from 'zod'
import { ASSET_LABEL_MAX_LENGTH, generatedAssetSchema } from './ai-studio-asset'

/** Credit cost of one flyer generation. */
export const FLYER_CREDIT_COST = 50

export const flyerFormatSchema = z.enum([
  'a4-portrait',
  'a4-landscape',
  'a5-portrait',
  'a6-portrait',
  'ig-story',
])

export type FlyerFormat = z.infer<typeof flyerFormatSchema>

export const FLYER_FORMAT_DIMENSIONS: Record<FlyerFormat, { width: number; height: number }> = {
  'a4-portrait': { width: 1240, height: 1754 },
  'a4-landscape': { width: 1754, height: 1240 },
  'a5-portrait': { width: 874, height: 1240 },
  'a6-portrait': { width: 620, height: 874 },
  'ig-story': { width: 1080, height: 1920 },
}

export const flyerQrPlacementSchema = z.enum([
  'bottom-right',
  'bottom-left',
  'top-right',
  'top-left',
  'center',
])

export type FlyerQrPlacement = z.infer<typeof flyerQrPlacementSchema>

export const flyerOutputSchema = z.enum(['jpg', 'pdf'])

export type FlyerOutput = z.infer<typeof flyerOutputSchema>

export const flyerFontSchema = z.enum(['inter', 'montserrat', 'oswald', 'playfair', 'bebas'])

export type FlyerFontChoice = z.infer<typeof flyerFontSchema>

export const FLYER_TITLE_MAX_LENGTH = 60
export const FLYER_DESCRIPTION_MAX_LENGTH = 400

const flyerHexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Expected #rrggbb hex color')

/** Focal point of the cover crop, as a 0–1 fraction of the overflowing axis. */
const flyerFocalPoint = z.number().min(0).max(1).default(0.5)

export const generateFlyerRequestSchema = z.object({
  format: flyerFormatSchema,
  /** Required only when the QR section is on; the route enforces that pairing. */
  qrData: z.string().max(1024).default(''),
  qrPlacement: flyerQrPlacementSchema.default('bottom-right'),
  output: flyerOutputSchema.default('jpg'),
  /** Names the entity the flyer belongs to; used for the stored asset title. */
  label: z.string().max(ASSET_LABEL_MAX_LENGTH).optional(),

  showImage: z.boolean().default(true),
  showQr: z.boolean().default(true),
  title: z.string().max(FLYER_TITLE_MAX_LENGTH).optional(),
  description: z.string().max(FLYER_DESCRIPTION_MAX_LENGTH).optional(),

  font: flyerFontSchema.default('inter'),
  background: flyerHexColor.default('#ffffff'),
  focalX: flyerFocalPoint,
  focalY: flyerFocalPoint,
})

export type GenerateFlyerRequest = z.infer<typeof generateFlyerRequestSchema>

export interface FlyerSections {
  showImage: boolean
  title?: string
  description?: string
}

/**
 * Share of the flyer height taken by the cover image. The preview and the
 * renderer must agree on this or the crop the user set would shift on export.
 */
export function flyerImageRatio(sections: FlyerSections): number {
  if (!sections.showImage) return 0
  const hasTitle = !!sections.title?.trim()
  const hasDescription = !!sections.description?.trim()
  if (hasTitle && hasDescription) return 0.62
  // A title alone used to leave 0.8, which squeezed the short formats' text band
  // below a printable QR size.
  if (hasTitle || hasDescription) return 0.72
  return 1
}

export const generateFlyerResponseSchema = z.object({
  asset: generatedAssetSchema,
})

export type GenerateFlyerResponse = z.infer<typeof generateFlyerResponseSchema>
